// Pure, dependency-free validation for feedback. Kept separate from the server
// action so it can be unit-tested without a DB, Blob, or Resend.

export const FEEDBACK_TYPES = ["bug", "idea", "other"] as const;
export type FeedbackType = (typeof FEEDBACK_TYPES)[number];

export const MAX_FEEDBACK_LENGTH = 5000;

/**
 * Keep a URL only if it's an http(s) URL, else null. The submit action stores
 * the page URL from client-supplied form data, which reaches the admin page as
 * an <a href>; this drops javascript:/data:/etc. schemes before they're stored.
 */
export function safeHttpUrl(raw: string): string | null {
  const url = raw.trim();
  return /^https?:\/\//i.test(url) ? url : null;
}

export type FeedbackValidationResult =
  | { ok: true; value: { type: FeedbackType; message: string } }
  | { ok: false; error: string };

export function validateFeedback(input: { type: string; message: string }): FeedbackValidationResult {
  const message = input.message.trim();
  if (!message) return { ok: false, error: "Enter a message." };
  if (message.length > MAX_FEEDBACK_LENGTH) {
    return { ok: false, error: `Message must be ${MAX_FEEDBACK_LENGTH} characters or fewer.` };
  }
  if (!FEEDBACK_TYPES.includes(input.type as FeedbackType)) {
    return { ok: false, error: "Choose a feedback type." };
  }
  return { ok: true, value: { type: input.type as FeedbackType, message } };
}

export const FEEDBACK_STATUSES = ["new", "in-progress", "addressed", "declined"] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

export const STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: "New",
  "in-progress": "In progress",
  addressed: "Addressed",
  declined: "Declined",
};

export const MAX_RESPONSE_LENGTH = 5000;

export function isValidStatus(s: string): s is FeedbackStatus {
  return (FEEDBACK_STATUSES as readonly string[]).includes(s);
}

export type FeedbackUpdateValidationResult =
  | { ok: true; value: { status: FeedbackStatus; response: string | null } }
  | { ok: false; error: string };

export function validateFeedbackUpdate(input: { status: string; response: string }): FeedbackUpdateValidationResult {
  if (!isValidStatus(input.status)) return { ok: false, error: "Choose a valid status." };
  const response = input.response.trim();
  if (response.length > MAX_RESPONSE_LENGTH) {
    return { ok: false, error: `Reply must be ${MAX_RESPONSE_LENGTH} characters or fewer.` };
  }
  return { ok: true, value: { status: input.status, response: response || null } };
}
